import { Alert, Link } from '@cloudscape-design/components';
import type { RuleDetails } from '@amzn/helianthus-arbiter-client';
import { ArnDisplay } from './ArnDisplay';
import { useRuleProvenance } from '../hooks/useRuleProvenance';
import { registryRuleUrl, RULE_AUTHORING_URL } from '../utils/arnUtils';

/**
 * Warns when something other than a person owns a rule. A rule this console did not write can be rewritten
 * without notice, so it is a banner on both the detail and edit pages rather than a footnote on either.
 */
export function ProvenanceAlert({ rule }: { rule: RuleDetails }) {
  const { principal, principalClass, field } = useRuleProvenance(rule);

  if (principalClass === 'registry') {
    return (
      <Alert type="warning" header="Deployed from the rules registry">
        The rules reconciler compares this rule with{' '}
        <Link href={registryRuleUrl(rule.ruleName!)} external externalIconAriaLabel="Opens in a new tab">
          {rule.ruleName}.json
        </Link>{' '}
        on every deploy and overwrites any difference, so changes made here are reverted. Change the registry
        file instead (see the{' '}
        <Link href={RULE_AUTHORING_URL} external externalIconAriaLabel="Opens in a new tab">
          rule authoring guide
        </Link>).
      </Alert>
    );
  }

  if (principalClass === 'automation') {
    return (
      <Alert type="warning" header={field === 'createdBy' ? 'Created by automation' : 'Last written by automation'}>
        <ArnDisplay value={principal} /> is a service role, not a person. That system may re-apply its own
        definition over changes made here.
      </Alert>
    );
  }

  return null;
}
